import React, { useState } from "react";
import { Printer, Loader2, Search, Wallet } from "lucide-react";
import { api, fmtDate } from "@/lib/apiClient";
import { rupiah } from "@/components/InvoiceDialog";
import { FakturSheet } from "@/components/FakturSheet";
import { StrukHeader } from "@/components/PrintHeader";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const NOTA_ROWS = 11;

const printArea = (id) => {
  const html = document.getElementById(id)?.innerHTML;
  if (!html) return;
  const w = window.open("", "_blank", "width=900,height=1000");
  if (!w) return toast.error("Popup diblokir browser");
  const doc = w.document;
  const isStruk = id === "struk-print";
  const style = doc.createElement("style");
  style.textContent = `@page { size: ${isStruk ? "58mm auto" : "A5 landscape"}; margin: ${
    isStruk ? "2mm" : "5mm"
  }; }
    body { font-family: ${isStruk ? "monospace" : "Arial, sans-serif"};
      color:#000; margin:0; padding:${isStruk ? "4px" : "0"};
      font-size:${isStruk ? "11px" : "12px"}; }
    table { width:100%; border-collapse:collapse; }
    .sheet { page-break-after: always; }
    .sheet:last-child { page-break-after: auto; }`;
  doc.head.appendChild(style);
  doc.body.innerHTML = html;
  w.setTimeout(() => w.print(), 500);
};

const Field = ({ label, children }) => (
  <div>
    <span className="text-[10px] uppercase tracking-[0.16em] text-slate-500">{label}</span>
    <div className="mt-1">{children}</div>
  </div>
);

const chunk = (arr, size) => {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out.length ? out : [[]];
};

export const NotaBoard = ({ mode = "nota" }) => {
  const { user } = useAuth();
  const [ref, setRef] = useState("");
  const [cariClient, setCariClient] = useState("");
  const [struks, setStruks] = useState([]);
  const [data, setData] = useState(null);
  const [jumlah, setJumlah] = useState("");
  const [akun, setAkun] = useState("KAS");
  const [busy, setBusy] = useState(false);
  const isStruk = mode === "struk";

  const loadStruks = React.useCallback(async () => {
    if (!isStruk) return;
    try {
      const { data: d } = await api.get("/struks", {
        params: cariClient.trim() ? { client: cariClient.trim() } : {},
      });
      setStruks(d);
    } catch {
      /* daftar struk opsional */
    }
  }, [isStruk, cariClient]);

  React.useEffect(() => {
    loadStruks();
  }, [loadStruks]);

  const bukaStruk = async (no) => {
    setBusy(true);
    try {
      const { data: d } = await api.get(`/struks/${no}`);
      setData(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Struk tidak ditemukan");
    } finally {
      setBusy(false);
    }
  };

  const cariRef = async (value) => {
    const key = (value || "").trim();
    if (!key) return;
    setBusy(true);
    try {
      const url = isStruk ? `/struk/${key}` : `/nota/${key}`;
      const { data: d } = await api.get(url);
      setData(d);
      setJumlah("");
    } catch (e) {
      setData(null);
      toast.error(e?.response?.data?.detail || "Data tidak ditemukan");
    } finally {
      setBusy(false);
    }
  };

  const cari = () => cariRef(ref);

  const bayar = async () => {
    setBusy(true);
    try {
      const { data: res } = await api.post("/nota/payment", {
        jo_number: data.jo_number,
        jumlah: Number(jumlah) || 0,
        akun,
      });
      toast.success(
        `${res.jenis} ${rupiah(Number(jumlah))} tercatat · struk ${res.struk_no} · sisa ${rupiah(res.sisa)}`
      );
      setJumlah("");
      await cariRef(ref);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal mencatat pembayaran");
    } finally {
      setBusy(false);
    }
  };

  const pages = data ? chunk(data.lines || [], NOTA_ROWS) : [];

  return (
    <div className="space-y-5" data-testid={`${mode}-board`}>
      <div className="glass rounded-2xl p-5 md:p-6 flex flex-wrap items-end gap-4">
        {!isStruk && (
          <>
            <Field label="Nomor JO">
              <div className="relative">
                <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  data-testid={`${mode}-ref-input`}
                  placeholder="0001 / NOTA-0001"
                  value={ref}
                  onChange={(e) => setRef(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && cari()}
                  className="h-11 pl-9 w-52 bg-slate-900/70 border-white/10"
                />
              </div>
            </Field>
            <Button
              data-testid={`${mode}-search-btn`}
              onClick={cari}
              disabled={busy || !ref.trim()}
              className="h-11 rounded-xl bg-blue-600 hover:bg-blue-500 px-5"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Tampilkan"}
            </Button>
          </>
        )}
        {isStruk && (
          <Field label="Filter Nama Client">
            <div className="relative">
              <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input
                data-testid="struk-client-filter"
                placeholder="cari nama client"
                value={cariClient}
                onChange={(e) => setCariClient(e.target.value)}
                className="h-11 pl-9 w-56 bg-slate-900/70 border-white/10"
              />
            </div>
          </Field>
        )}

        {data && (
          <>
            {!isStruk && (
              <>
                <Field label="Nominal Pembayaran">
                  <Input
                    data-testid="nota-amount-input"
                    placeholder={`maks ${rupiah(data.sisa)}`}
                    value={jumlah}
                    onChange={(e) => setJumlah(e.target.value)}
                    className="h-11 w-40 bg-slate-900/70 border-white/10"
                  />
                </Field>
                <Field label="Via">
                  <select
                    data-testid="nota-akun-select"
                    value={akun}
                    onChange={(e) => setAkun(e.target.value)}
                    className="h-11 rounded-xl bg-slate-900/70 border border-white/10 px-3 text-sm outline-none"
                  >
                    {["KAS", "BANK"].map((a) => (
                      <option key={a} value={a}>{a}</option>
                    ))}
                  </select>
                </Field>
                <Button
                  data-testid="nota-pay-btn"
                  onClick={bayar}
                  disabled={busy || data.sisa <= 0 || !jumlah}
                  className="h-11 rounded-xl bg-emerald-600 hover:bg-emerald-500 px-5"
                >
                  <Wallet className="w-4 h-4 mr-2" /> Payment
                </Button>
              </>
            )}
            <Button
              data-testid={`${mode}-print-btn`}
              onClick={() => printArea(isStruk ? "struk-print" : "nota-print")}
              variant="outline"
              className="h-11 rounded-xl border-white/10 bg-slate-800/60 px-5"
            >
              <Printer className="w-4 h-4 mr-2" /> Print / PDF
            </Button>
          </>
        )}
      </div>

      {isStruk && (
        <div className="glass rounded-2xl p-5 md:p-6 space-y-3" data-testid="struk-list-panel">
          <h4 className="font-display text-sm font-semibold uppercase tracking-wider text-slate-300">
            Daftar Nomor Struk Tercetak
          </h4>
          <div className="overflow-x-auto rounded-xl border border-white/10 max-h-72 overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-900/80 text-slate-400 uppercase text-[10px] tracking-wider">
                <tr>
                  {["No Struk", "Tanggal", "Ref", "Client", "Jenis", "Via", "Nominal", "Kasir", ""].map((h) => (
                    <th key={h} className="px-3 py-2 text-left font-semibold">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody data-testid="struk-list">
                {struks.length === 0 && (
                  <tr>
                    <td colSpan={9} className="px-3 py-6 text-center text-slate-500">
                      Belum ada struk. Nomor struk terbit otomatis setiap ada payment.
                    </td>
                  </tr>
                )}
                {struks.map((s) => (
                  <tr key={s.struk_no} className="border-t border-white/5 hover:bg-slate-800/40">
                    <td className="px-3 py-2 font-semibold text-teal-300">{s.struk_no}</td>
                    <td className="px-3 py-2 text-slate-400">{fmtDate(s.tanggal)}</td>
                    <td className="px-3 py-2">{s.ref}</td>
                    <td className="px-3 py-2">{s.client_name}</td>
                    <td className="px-3 py-2">{s.jenis}</td>
                    <td className="px-3 py-2">{s.akun}</td>
                    <td className="px-3 py-2">{rupiah(s.jumlah)}</td>
                    <td className="px-3 py-2 text-slate-400">{s.by}</td>
                    <td className="px-3 py-2">
                      <button
                        type="button"
                        data-testid={`struk-open-${s.seq}`}
                        onClick={() => bukaStruk(s.struk_no)}
                        className="rounded-lg border border-white/10 bg-slate-800/70 px-3 py-1 text-[10px] font-semibold uppercase hover:bg-slate-700"
                      >
                        Cetak
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!data ? (
        <div className="glass rounded-2xl p-10 text-center text-slate-500" data-testid={`${mode}-empty`}>
          {isStruk
            ? "Pilih salah satu nomor struk pada daftar di atas untuk mencetak."
            : "Masukkan nomor JO untuk menampilkan nota."}
        </div>
      ) : isStruk ? (
        <div className="glass rounded-2xl p-6 flex justify-center">
          <div
            id="struk-print"
            data-testid="struk-preview"
            className="bg-white text-black w-[240px] p-3 text-[11px] font-mono"
          >
            <StrukHeader />
            <div className="mt-2 border-t border-dashed border-black pt-2">
              <div>No Struk : {data.struk_no || "-"}</div>
              <div>No : {data.nota_no}</div>
              <div>Nama : {data.client_name}</div>
              <div>Tgl Order : {fmtDate(data.tanggal)}</div>
              <div>Tgl Cetak : {new Date().toLocaleDateString("id-ID")}</div>
              <div>Kasir : {user?.username || "-"}</div>
            </div>
            <div className="mt-2 border-t border-dashed border-black pt-1" data-testid="struk-lines">
              <div className="text-[9px] font-bold uppercase">Rincian Order</div>
              {(data.lines || []).map((l, i) => (
                <div key={`s-${i}`} className="mt-1 leading-[1.25] break-words" data-testid={`struk-line-${i}`}>
                  <div>
                    {i + 1}. {[l.kategori, l.jenis_order].filter(Boolean).join(" · ")}
                    {l.deskripsi ? ` · ${l.deskripsi}` : ""}
                    {l.panjang || l.lebar ? ` · ${l.panjang || "-"}x${l.lebar || "-"}` : ""}
                    {` · qty ${l.qty || 1}`}
                    {l.jumlah ? ` · jml ${l.jumlah}` : ""}
                    {Number(l.price) > 0 ? ` @${rupiah(l.price)}` : ""}
                    {Number(l.diskon) > 0 ? ` · disk ${rupiah(l.diskon)}` : ""}
                    {` = ${rupiah(l.nilai)}`}
                  </div>
                </div>
              ))}
            </div>
            {data.catatan ? (
              <div
                className="mt-2 border-t border-dashed border-black pt-1 break-words"
                data-testid="struk-catatan"
              >
                <span className="font-bold">Catatan: </span>
                {data.catatan}
              </div>
            ) : null}
            <div className="mt-2 border-t border-dashed border-black pt-1">
              {data.struk_jumlah != null && (
                <div className="flex justify-between">
                  <span>BAYAR ({data.struk_jenis})</span>
                  <b>{rupiah(data.struk_jumlah)}</b>
                </div>
              )}
              <div className="flex justify-between"><span>TOTAL</span><b>{rupiah(data.total)}</b></div>
              <div className="flex justify-between"><span>PAYMENT</span><span>{rupiah(data.terbayar)}</span></div>
              <div className="flex justify-between"><span>SISA</span><span>{rupiah(data.sisa)}</span></div>
            </div>
            <div className="mt-2 text-center text-[9px] border-t border-dashed border-black pt-1">
              Terima kasih · {data.status} · dicetak oleh {user?.username || "-"}
            </div>
          </div>
        </div>
      ) : (
        <div className="glass rounded-2xl p-6 overflow-x-auto">
          <div id="nota-print" data-testid="nota-preview" className="mx-auto w-fit space-y-6">
            {pages.map((rows, idx) => (
              <div
                key={`p-${idx}`}
                className="sheet bg-white"
                data-testid={`nota-sheet-${idx + 1}`}
                style={{ width: 780, padding: 16 }}
              >
                <FakturSheet
                  nomor={data.nota_no}
                  tanggal={fmtDate(data.tanggal)}
                  tanggalPrint={new Date().toLocaleDateString("id-ID")}
                  client={data.client_name}
                  lines={rows}
                  rowCount={NOTA_ROWS}
                  total={data.total}
                  terbayar={data.terbayar}
                  sisa={data.sisa}
                  showTotals={idx === pages.length - 1}
                  catatan={data.client_phone ? `No. HP: ${data.client_phone}` : ""}
                  printedBy={user?.username || ""}
                  pageInfo={pages.length > 1 ? `Lembar ${idx + 1} dari ${pages.length}` : ""}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
